import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'

interface Report {
  id: string
  report_id: string
  patient: {
    id: string
    patient_id: string
    full_name: string
    date_of_birth: string
    gender: string
  }
  status: 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'FINALIZED'
  diagnosis: string
  pathologist?: {
    username: string
    first_name: string
    last_name: string
  }
  created_at: string
  updated_at: string
  finalized_at?: string
  reviewed_at?: string
  cytopathologist?: string
  delivered_at?: string
}

const ResultsWorklist: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    fetchApprovedReports()
  }, [])

  const fetchApprovedReports = async () => {
    try {
      setIsLoading(true)
      // Fetch approved cytology reports
      const response = await fetch('/api/v1/pathology/cytology-reports/?page_size=1000')
      if (response.ok) {
        const data = await response.json()
        const approvedReports = (data.results || data)
          .filter((r: any) => r.status === 'finalized') // Only finalized/approved reports
          .map((r: any) => ({
            id: r.id,
            report_id: `CYT-${r.id}`,
            patient: {
              id: r.patient,
              patient_id: r.patient_code || '',
              full_name: r.patient_name || '',
              date_of_birth: '',
              gender: r.gender || ''
            },
            status: 'APPROVED',
            diagnosis: r.conclusion || '',
            cytopathologist: r.cytopathologist,
            pathologist: r.cytopathologist ? {
              username: r.cytopathologist,
              first_name: r.cytopathologist.split(' ')[0] || '',
              last_name: r.cytopathologist.split(' ').slice(1).join(' ') || ''
            } : undefined,
            created_at: r.report_date,
            updated_at: r.updated_at,
            finalized_at: r.updated_at,
            delivered_at: r.delivered_at
          }))
        setReports(approvedReports)
      }
    } catch (error) {
      console.error('Error fetching approved reports:', error)
      toast.error('Không thể tải danh sách phiếu kết quả')
    } finally {
      setIsLoading(false)
    }
  }

  const markAsDelivered = async (reportId: string) => {
    try {
      const response = await fetch(`/api/v1/pathology/cytology-reports/${reportId}/mark_delivered/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        toast.success('Đã xác nhận trả kết quả thành công')
        // Refresh the list
        fetchApprovedReports()
      } else {
        toast.error('Không thể xác nhận trả kết quả')
      }
    } catch (error) {
      console.error('Error marking as delivered:', error)
      toast.error('Lỗi khi xác nhận trả kết quả')
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const filteredReports = reports.filter(report => {
    const searchLower = searchTerm.toLowerCase()
    return (
      report.report_id.toLowerCase().includes(searchLower) ||
      report.patient.full_name.toLowerCase().includes(searchLower) ||
      report.patient.patient_id.toLowerCase().includes(searchLower) ||
      (report.diagnosis && report.diagnosis.toLowerCase().includes(searchLower))
    )
  })

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📋 Phiếu Kết Quả</h1>
        <p className="text-gray-600 mt-1">Danh sách phiếu kết quả đã được duyệt</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="text-3xl font-bold text-green-700">{reports.length}</div>
          <div className="text-sm text-green-600 mt-1">✅ Phiếu đã tạo</div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-3xl font-bold text-blue-700">{filteredReports.length}</div>
          <div className="text-sm text-blue-600 mt-1">🔍 Kết quả tìm kiếm</div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="text-3xl font-bold text-purple-700">
            {reports.filter(r => {
              const today = new Date()
              const reportDate = new Date(r.created_at)
              return reportDate.toDateString() === today.toDateString()
            }).length}
          </div>
          <div className="text-sm text-purple-600 mt-1">📅 Hôm nay</div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="🔍 Tìm kiếm theo mã phiếu, tên bệnh nhân, mã BN..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2">Đang tải...</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg">
              {searchTerm ? '🔍 Không tìm thấy phiếu nào' : '📋 Chưa có phiếu kết quả nào được duyệt'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mã phiếu
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bệnh nhân
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bác sĩ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kết luận
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ngày tạo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-blue-600">
                          {report.report_id}
                        </span>
                        <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          ✓ Đã hoàn thành
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {report.patient.full_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        Mã BN: {report.patient.patient_id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {report.cytopathologist || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-md truncate">
                        {report.diagnosis || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(report.finalized_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <Link
                          to={`/pathology/cytology-reports/${report.id}`}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          👁️ Xem
                        </Link>
                        <Link
                          to={`/pathology/cytology-reports/${report.id}`}
                          target="_blank"
                          className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700"
                          title="Mở phiếu để in"
                        >
                          🖨️ In
                        </Link>
                        {report.delivered_at ? (
                          <div className="px-3 py-1 bg-green-100 text-green-800 rounded border border-green-300">
                            ✅ Đã trả
                          </div>
                        ) : (
                          <button
                            onClick={() => markAsDelivered(report.id)}
                            className="px-3 py-1 bg-orange-600 text-white rounded hover:bg-orange-700"
                            title="Xác nhận đã trả kết quả cho bệnh nhân"
                          >
                            📤 Xác nhận trả
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      {!isLoading && filteredReports.length > 0 && (
        <div className="mt-4 text-sm text-gray-600 text-center">
          Hiển thị {filteredReports.length} / {reports.length} phiếu kết quả
        </div>
      )}
    </div>
  )
}

export default ResultsWorklist
